import { CalendarClock, ClipboardList, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  formatDateTime,
  isStructuredCollectionPendency,
  isWaitlistReleasePendency,
  outcomeLabel,
} from './pendencies-adapter';
import { PendencyCatBadge, PendencyLifecycleBadge, PendencyOriginBadge } from './PendencyBadges';

import type { PendencyActionKind } from './PendencyCard';
import type { PendencyWireItem } from '../../lib/api-client';
import type { JSX } from 'react';

function Section({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-4">
      <div
        className="mb-1 text-xs font-medium uppercase"
        style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}
      >
        {label}
      </div>
      <div className="text-sm" style={{ color: 'var(--fg-1)' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * PendencyDrawer — detalhe da pendência (read) + ações no rodapé. Apenas
 * campos REAIS do `SerializedPendency` — sem histórico/audit fabricados (§4).
 */
export function PendencyDrawer({
  pendency,
  onClose,
  onAction,
}: {
  pendency: PendencyWireItem;
  onClose: () => void;
  onAction: (kind: PendencyActionKind, item: PendencyWireItem) => void;
}): JSX.Element {
  const navigate = useNavigate();
  const convId = pendency.conversationId;
  const isWaitlistRelease = isWaitlistReleasePendency(pendency.source);
  // ADR-155 Fase 4 (§3.7) — render dedicado da revisão de coleta gerada pelo
  // finalize (E5 já embute o conflito dupla-versão B2B na description).
  const isStructuredCollection = isStructuredCollectionPendency(pendency.source);
  return (
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex justify-end bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-label={pendency.title}
        className="flex h-full w-full max-w-md flex-col shadow-xl"
        style={{ background: 'var(--bg-surface)' }}
      >
        <header
          className="flex items-start gap-2 p-4"
          style={{ borderBottom: '1px solid var(--border-default)' }}
        >
          <button
            type="button"
            className="btn btn--ghost btn--sm btn--icon"
            onClick={onClose}
            aria-label="Fechar"
          >
            <X size={14} />
          </button>
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-semibold" style={{ color: 'var(--fg-1)' }}>
              {pendency.title}
            </h2>
          </div>
        </header>

        <div className="flex-1 overflow-auto p-4">
          <Section label="Estado e prazo">
            <div className="flex flex-wrap items-center gap-2">
              <PendencyLifecycleBadge
                status={pendency.status}
                postponeCount={pendency.postponeCount}
              />
              <span
                className="mono text-xs"
                style={{ color: pendency.overdue ? 'var(--danger-fg)' : 'var(--fg-2)' }}
              >
                {pendency.deadline !== null ? formatDateTime(pendency.deadline) : 'Sem prazo'}
              </span>
            </div>
          </Section>

          <Section label="Categoria e origem">
            <div className="flex flex-wrap items-center gap-2">
              <PendencyCatBadge category={pendency.category} />
              <PendencyOriginBadge source={pendency.source} />
            </div>
          </Section>

          {/* Frente B Sessão 4 — render dedicado da liberação de fila de espera.
              A vaga abriu e o operador escolhe quem liberar; a AÇÃO (escolher o
              candidato + cravar a vaga) mora na /agenda (WaitlistPanel, por recurso),
              onde estão os candidatos ranqueados ao vivo + a guarda de booking. Aqui
              conduzimos coerentemente a ela (não duplicamos a liberação). */}
          {isWaitlistRelease && (
            <Section label="Liberação de fila de espera">
              <p style={{ color: 'var(--fg-2)' }}>
                Uma vaga abriu e há clientes na fila de espera. A liberação é feita na agenda, onde
                os candidatos aparecem ranqueados por prioridade e a vaga é confirmada com a guarda
                de capacidade.
              </p>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                style={{ marginTop: 'var(--space-2)' }}
                onClick={() => navigate('/agenda')}
                data-testid="pendency-waitlist-resolve-on-agenda"
              >
                <CalendarClock size={14} aria-hidden /> Liberar na agenda
              </button>
            </Section>
          )}

          {/* ADR-155 Fase 4 (§3.7) — render dedicado "Revisão de coleta": a
              description (roteamento por-campo + conflito dupla-versão E5)
              + deep-link para reabrir a coleta na conversa de origem. Molde
              do `waitlist_promotion`. Suprime a Section "Descrição" genérica
              abaixo (evita duplicar o mesmo texto). */}
          {isStructuredCollection && (
            <Section label="Revisão de coleta">
              {pendency.description !== null && pendency.description.trim().length > 0 && (
                <p style={{ color: 'var(--fg-2)' }}>{pendency.description}</p>
              )}
              {convId !== null && (
                <button
                  type="button"
                  className="btn btn--primary btn--sm"
                  style={{ marginTop: 'var(--space-2)' }}
                  onClick={() => navigate(`/atendimento/conversation/${convId}/coleta`)}
                  data-testid="pendency-structured-collection-open-coleta"
                >
                  <ClipboardList size={14} aria-hidden /> Abrir coleta da conversa
                </button>
              )}
            </Section>
          )}

          {!isStructuredCollection &&
            pendency.description !== null &&
            pendency.description.trim().length > 0 && (
              <Section label="Descrição">{pendency.description}</Section>
            )}

          {convId !== null && (
            <Section label="Conversa relacionada">
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => navigate(`/atendimento/conversation/${convId}`)}
              >
                Abrir conversa
              </button>
            </Section>
          )}

          {pendency.resolutionOutcome !== null && (
            <Section label="Desfecho">
              {outcomeLabel(pendency.resolutionOutcome)}
              {pendency.resolutionNote !== null && pendency.resolutionNote.trim().length > 0 && (
                <div className="mt-1" style={{ color: 'var(--fg-2)' }}>
                  {pendency.resolutionNote}
                </div>
              )}
            </Section>
          )}

          <Section label="Criada em">
            <span className="mono text-xs">{formatDateTime(pendency.createdAt)}</span>
          </Section>
        </div>

        <footer
          className="flex items-center gap-2 p-4"
          style={{ borderTop: '1px solid var(--border-default)' }}
        >
          {pendency.status === 'postponed' ? (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => onAction('resume', pendency)}
            >
              Retomar
            </button>
          ) : (
            (pendency.status === 'open' || pendency.status === 'in_progress') && (
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => onAction('postpone', pendency)}
              >
                Adiar
              </button>
            )
          )}
          {pendency.status !== 'resolved' &&
            pendency.status !== 'cancelled' &&
            pendency.status !== 'archived' &&
            pendency.status !== 'expired' && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => onAction('resolve', pendency)}
              >
                Resolver
              </button>
            )}
        </footer>
      </div>
    </div>
  );
}
